import 'arrive';
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const STAGGER_GAP = 0.12;

/**
 * Raggruppa le card per riga basandosi su getBoundingClientRect().top (±10px di tolleranza).
 * Deve essere chiamata PRIMA di gsap.set() perché getBoundingClientRect include i transform.
 */
function groupCardsByRow(cards) {
    const rows = [];
    cards.forEach(card => {
        const top = Math.round(card.getBoundingClientRect().top);
        const existing = rows.find(r => Math.abs(r.top - top) <= 10);
        if (existing) {
            existing.cards.push(card);
        } else {
            rows.push({ top, cards: [card] });
        }
    });
    return rows.sort((a, b) => a.top - b.top);
}

/** Anima una riga di card con stagger a partire da baseDelay. */
function animateRow(rowCards, baseDelay, onReveal) {
    rowCards.forEach((c, i) => {
        gsap.to(c, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: 'power2.out',
            delay: baseDelay + i * STAGGER_GAP,
            onComplete: () => {
                c.classList.add('animated', 'revealed');
                if (typeof onReveal === 'function') onReveal(c);
            },
        });
    });
}

/**
 * Anima le card del post-loop raggruppandole per riga della griglia:
 * - Righe già visibili nel viewport: fade-in immediato, con stagger cumulativo tra righe.
 * - Righe sotto il fold: ScrollTrigger sul primo elemento, stagger fresco all'entrata.
 *
 * Il raggruppamento per riga (stesso rect.top ± 10px) è necessario perché
 * ScrollTrigger.batch raggruppa per tempo di entrata, non per posizione nella griglia,
 * causando battaglie errate tra card di righe diverse in layout misti (col-md-6/col-md-4).
 */
function setupCards(section, onReveal) {
    const cards = Array.from(section.querySelectorAll('.post-loop__item')).filter(
        c => !c.dataset.stBound && !c.classList.contains('revealed')
    );
    if (!cards.length) return;

    cards.forEach(c => { c.dataset.stBound = '1'; });

    // Snapshot posizioni PRIMA di applicare transform (gsap.set con y:60 sposta il rect.top)
    const rows = groupCardsByRow(cards);

    gsap.set(cards, { opacity: 0, y: 60 });

    let inViewDelay = 0;

    rows.forEach(({ top, cards: rowCards }) => {
        if (top < window.innerHeight) {
            // Riga già visibile: anima subito, stagger sequenziale tra righe
            animateRow(rowCards, inViewDelay, onReveal);
            inViewDelay += rowCards.length * STAGGER_GAP;
        } else {
            // Riga sotto il fold: ScrollTrigger sul primo elemento della riga
            ScrollTrigger.create({
                trigger: rowCards[0],
                start: 'top bottom',
                once: true,
                onEnter: () => animateRow(rowCards, 0, onReveal),
            });
        }
    });
}

// Inizializza per il blocco al caricamento iniziale
document.arrive('.block-post-loop', { existing: true }, function (el) {
    setupCards(el);
});

document.addEventListener('DOMContentLoaded', () => {
    const container = document.querySelector('.block-post-loop');
    if (!container) return;

    const grid = container.querySelector('.post-loop-grid');
    const counter = container.querySelector('.post-loop-counter .shown');
    const total = parseInt(container.dataset.total);
    const featuredCount = parseInt(container.dataset.featuredCount || '0');
    const ajaxUrl = container.dataset.ajaxUrl;
    const nonce = container.dataset.nonce;

    let loading = false;
    let pendingBatch = 0;
    const pendingSet = new Set();
    const markRevealed = (card) => {
        if (!card.classList.contains('revealed')) {
            card.classList.add('revealed');
        }
        if (pendingSet.has(card)) {
            pendingSet.delete(card);
            pendingBatch = Math.max(0, pendingBatch - 1);
        }
    };

    const loadMorePosts = async () => {
        const btn = container.querySelector('.post-loop-load-more');
        if (!btn || loading) return;

        const offset = parseInt(btn.dataset.offset);
        const shownNow = parseInt(counter.textContent || '0');
        if (shownNow >= total || pendingBatch > 0) return;

        loading = true;
        btn.disabled = true;
        const btnLabel = btn.querySelector('span');
        const originalLabel = btnLabel ? btnLabel.textContent : '';
        if (btnLabel) btnLabel.textContent = 'Caricamento...';

        const featuredIds = btn.dataset.featuredIds || '';

        const formData = new FormData();
        formData.append('action', 'ste_post_loop_load_more');
        formData.append('nonce', nonce);
        formData.append('offset', offset);
        formData.append('post_type', btn.dataset.postType);
        formData.append('layout', btn.dataset.layout);
        formData.append('featured_ids', featuredIds);

        try {
            const res = await fetch(ajaxUrl, { method: 'POST', body: formData });
            const data = await res.json();

            if (data.success) {
                const temp = document.createElement('div');
                temp.innerHTML = data.data.html;

                const newNodes = Array.from(temp.children);
                grid.append(...newNodes);

                const appendedCards = newNodes.filter(n => n.nodeType === 1 && n.classList.contains('post-loop__item'));
                pendingBatch += appendedCards.length;
                appendedCards.forEach(c => pendingSet.add(c));

                const nonFeaturedLoaded = offset + data.data.count;
                const shownCount = Math.min(total, featuredCount + nonFeaturedLoaded);
                counter.textContent = shownCount;
                btn.dataset.offset = nonFeaturedLoaded;

                if (shownCount >= total || data.data.count === 0) {
                    btn.remove();
                } else {
                    if (btnLabel) btnLabel.textContent = originalLabel;
                }

                // Anima le card: visibili subito, sotto il fold con ScrollTrigger
                setupCards(container, markRevealed);
                // Fallback sicurezza: sblocca pendingBatch dopo 2s
                appendedCards.forEach(c => setTimeout(() => markRevealed(c), 2000));

                ScrollTrigger.refresh();
            } else {
                if (btnLabel) btnLabel.textContent = 'Errore, riprova';
            }
        } catch (err) {
            if (btnLabel) btnLabel.textContent = 'Errore, riprova';
            console.error(err);
        } finally {
            loading = false;
            btn.disabled = false;
        }
    };

    // Click listener sul bottone load more
    container.addEventListener('click', (e) => {
        if (e.target.closest('.post-loop-load-more')) {
            loadMorePosts();
        }
    });
});
